import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function EditIssueDialog({ issue, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (issue && open) {
      setForm({
        title: issue.title || "",
        description: issue.description || "",
        priority: issue.priority || "medium",
      });
    }
  }, [issue, open]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await api.patch(`/issues/${issue.id}`, {
        title: form.title.trim(),
        description: form.description,
        priority: form.priority,
      });
      toast.success("Issue updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-issue-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">Edit issue</DialogTitle>
          <DialogDescription>
            Update the title, description and priority for this ticket.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Title</Label>
            <Input
              data-testid="edit-issue-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Front-door card reader is dead"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Description</Label>
            <Textarea
              data-testid="edit-issue-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's happening? Any details that help triage…"
              rows={6}
              className="resize-y min-h-[140px]"
            />
            <p className="text-xs text-gray-500">{form.description.length} characters</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Priority</Label>
            <Select
              value={form.priority}
              onValueChange={(v) => setForm({ ...form, priority: v })}
            >
              <SelectTrigger data-testid="edit-issue-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-testid="save-edit-issue"
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            onClick={save}
            disabled={saving}
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
